import {
  useAllFeatureFlags,
  useFeatureFlagValue,
  FeatureFlag,
} from "../../feature-flags";
import type { FeatureFlagConfig } from "../../feature-flags";

export interface OnboardingFeatureEnabledResult {
  /** True when both EnableOnboarding and the feature flag are on. */
  isEnabled: boolean;
  /** True once both flags have been delivered by LaunchDarkly. */
  isLoaded: boolean;
  /** True when both EnableOnboarding and the feature flag are on. */
  areFlagsOn: boolean;
}

/**
 * Composite gate for an onboarding tool that also requires the parent portal flag.
 *
 * The feature is available only when:
 *  1. `enable-onboarding` is on.
 *  2. The given per-feature flag is on.
 *
 * `isLoaded` prevents a redirect flash: layouts render nothing until both flags
 * are present in the LD payload, mirroring `useOnboardingEnabled`.
 */
export function useOnboardingFeatureEnabled(
  featureConfig: FeatureFlagConfig,
): OnboardingFeatureEnabledResult {
  const flags = useAllFeatureFlags();
  const onboardingOn = useFeatureFlagValue(FeatureFlag.EnableOnboarding);
  const featureOn = useFeatureFlagValue(featureConfig);

  const isLoaded =
    FeatureFlag.EnableOnboarding.flag in flags && featureConfig.flag in flags;

  const areFlagsOn = onboardingOn && featureOn;

  return {
    isEnabled: areFlagsOn,
    isLoaded,
    areFlagsOn,
  };
}
